// Adapters for reading the Supabase persisted plan contract.
//
// The RPC returns a payload with (per the handoff):
//   generation_id, plans[] {
//     plan_id, option_label, explanation,
//     daily_totals, target_gaps,
//     items[] { day_number, slot, component_order, role,
//               etm_id, product_name, serving_size, quantity,
//               calories, protein, carbohydrates, fat, allergens }
//   }
// Exact key names may vary slightly server-side; these readers are
// tolerant so the frontend never fails on cosmetic naming differences.
// They NEVER invent nutrition values.  Missing values render as `—`.

function pickString(o, keys) {
  if (!o) return null;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}
function pickNumber(o, keys) {
  if (!o) return null;
  for (const k of keys) {
    const v = o[k];
    if (v === null || v === undefined || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}
function pickArray(o, keys) {
  if (!o) return null;
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  return null;
}
function pickObject(o, keys) {
  if (!o) return null;
  for (const k of keys) {
    const v = o[k];
    if (v && typeof v === 'object' && !Array.isArray(v)) return v;
  }
  return null;
}

export function readGenerationId(payload) {
  return pickString(payload, ['generation_id']);
}

export function readOutcome(payload) {
  return pickString(payload, ['outcome']) || 'generated';
}

export function readPlans(payload) {
  const arr = pickArray(payload, ['plans', 'plan_options', 'options']);
  return Array.isArray(arr) ? arr : [];
}

export function readPlanId(plan) {
  return pickString(plan, ['plan_id', 'meal_plan_id', 'id']);
}
export function readPlanLabel(plan) {
  return pickString(plan, ['option_label', 'label', 'plan_option_label']);
}
export function readPlanExplanation(plan) {
  return pickString(plan, ['explanation', 'why', 'summary']);
}

// Read totals from an object shaped like { calories, protein_g, carbs_g, fat_g }
// (or the older client-side field names).  Never invents values.
function readTotalsFrom(obj) {
  if (!obj) return { calories: null, protein: null, carbohydrates: null, fat: null };
  return {
    calories: pickNumber(obj, ['calories']),
    protein: pickNumber(obj, ['protein_g', 'protein']),
    carbohydrates: pickNumber(obj, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNumber(obj, ['fat_g', 'fat']),
  };
}
function readGapsFrom(obj) {
  if (!obj) return null;
  return {
    calories: pickNumber(obj, ['calories']),
    protein: pickNumber(obj, ['protein_g', 'protein']),
    carbohydrates: pickNumber(obj, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNumber(obj, ['fat_g', 'fat']),
  };
}
export function readPlanDailyTotals(plan) {
  const t = pickObject(plan, ['daily_totals', 'totals']);
  return readTotalsFrom(t);
}
export function readPlanTargetGaps(plan) {
  const t = pickObject(plan, ['target_gaps', 'gaps', 'target_gap']);
  return t ? readGapsFrom(t) : null;
}

// Items may be a flat array on the plan or nested under `days`.
// Return an array of day objects, each with items[] plus optional
// per-day totals/target_gaps carried through from the day payload.
export function readPlanItemsByDay(plan) {
  const days = pickArray(plan, ['days']);
  if (Array.isArray(days)) {
    // Preferred shape (production RPC).
    const out = [];
    for (const d of days) {
      const dn = pickNumber(d, ['day_number']) ?? 1;
      const dayItems = pickArray(d, ['items', 'meal_plan_items']) || [];
      const rows = dayItems.slice().sort((a, b) => {
        const sa = pickNumber(a, ['slot']) ?? 0;
        const sb = pickNumber(b, ['slot']) ?? 0;
        if (sa !== sb) return sa - sb;
        const ca = pickNumber(a, ['component_order']) ?? 0;
        const cb = pickNumber(b, ['component_order']) ?? 0;
        return ca - cb;
      });
      out.push({
        day_number: dn,
        items: rows.map((r) => readItem({ ...r, day_number: r.day_number ?? dn })),
        totals: readTotalsFrom(pickObject(d, ['totals', 'daily_totals'])),
        target_gaps: (() => {
          const g = pickObject(d, ['target_gaps', 'gaps', 'target_gap']);
          return g ? readGapsFrom(g) : null;
        })(),
      });
    }
    out.sort((a, b) => a.day_number - b.day_number);
    return out;
  }

  // Fallback: items are a flat array on the plan itself.
  const flat = pickArray(plan, ['items', 'meal_plan_items', 'plan_items']) || [];
  const map = new Map();
  for (const raw of flat) {
    const day = pickNumber(raw, ['day_number']) ?? 1;
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(raw);
  }
  const sortedDays = [...map.keys()].sort((a, b) => a - b);
  return sortedDays.map((day_number) => {
    const rows = map.get(day_number).slice();
    rows.sort((a, b) => {
      const sa = pickNumber(a, ['slot']) ?? 0;
      const sb = pickNumber(b, ['slot']) ?? 0;
      if (sa !== sb) return sa - sb;
      const ca = pickNumber(a, ['component_order']) ?? 0;
      const cb = pickNumber(b, ['component_order']) ?? 0;
      return ca - cb;
    });
    return {
      day_number,
      items: rows.map(readItem),
      totals: { calories: null, protein: null, carbohydrates: null, fat: null },
      target_gaps: null,
    };
  });
}

export function readItem(raw) {
  return {
    day_number: pickNumber(raw, ['day_number']) ?? 1,
    slot: pickNumber(raw, ['slot']) ?? 1,
    component_order: pickNumber(raw, ['component_order']) ?? 1,
    role: pickString(raw, ['role', 'planner_role', 'meal_role']) || 'core',
    etm_id: pickString(raw, ['etm_id']),
    product_name: pickString(raw, ['product_name', 'name']),
    serving_size: pickString(raw, ['serving_size', 'serving']),
    quantity: pickNumber(raw, ['quantity']) ?? 1,
    calories: pickNumber(raw, ['calories']),
    protein: pickNumber(raw, ['protein_g', 'protein']),
    carbohydrates: pickNumber(raw, ['carbs_g', 'carbohydrates', 'carbs']),
    fat: pickNumber(raw, ['fat_g', 'fat']),
    // Allergen display: the production RPC returns a single string field
    // named `major_allergens` (e.g. "Milk, Fish") or null.  Older/other
    // shapes may return an `allergens[]` array.  Accept both.
    major_allergens: pickString(raw, ['major_allergens', 'allergen_display']),
    allergens: (() => {
      const arr = pickArray(raw, ['allergens', 'allergen_list']);
      return Array.isArray(arr) ? arr : [];
    })(),
  };
}

export const PLAN_LABEL_DISPLAY = {
  best_match: 'Best Macro Match',
  higher_protein: 'Higher Protein',
  leaner_option: 'Lower Calorie',
  glp1_high_protein_option: 'GLP-1 High-Protein Option',
};
